import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult } from '@tanstack/react-query';
import * as api from './admin';
import type { AdminPoll, Candidate, MediaItem, ReportStatus } from './types';

/**
 * Every read and every write the panel makes, with the invalidation attached to
 * the write that causes it. That pairing is the whole reason Query is here: a
 * CRUD panel's characteristic bug is a list that still shows what was just
 * deleted, and it happens whenever a mutation and its re-read live apart.
 */
export const keys = {
  polls: ['admin', 'polls'] as const,
  poll: (id: string) => ['admin', 'poll', id] as const,
  media: ['admin', 'media'] as const,
  tags: ['tags'] as const,
  compliance: ['admin', 'compliance'] as const,
  reports: ['admin', 'reports'] as const,
  whatsapp: ['admin', 'whatsapp'] as const,
};

export const usePolls = () => useQuery({ queryKey: keys.polls, queryFn: api.listAdminPolls });

export const usePoll = (id: string | undefined) =>
  useQuery({
    queryKey: keys.poll(id ?? ''),
    queryFn: () => api.getPollDetail(id as string),
    enabled: Boolean(id),
  });

export const useMedia = () => useQuery({ queryKey: keys.media, queryFn: api.listMedia });

/** Both lists after a poll write: the row's counts live on the list, the fields on the detail. */
function usePollMutation<TArgs, TResult>(
  mutationFn: (args: TArgs) => Promise<TResult>,
  pollId?: string,
): UseMutationResult<TResult, Error, TArgs> {
  const client = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.polls });
      if (pollId) void client.invalidateQueries({ queryKey: keys.poll(pollId) });
      // A poll write can create a category: the picker must offer it at once.
      void client.invalidateQueries({ queryKey: keys.tags });
    },
  });
}

export const useTags = () => useQuery({ queryKey: keys.tags, queryFn: api.listTags });

/**
 * A category created on its own has no poll on it, so no poll list can be stale
 * — but the pickers read the tag list, and the point of creating one here is to
 * have it offered on the next poll.
 */
export const useCreateTag = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ name, icon }: { name: string; icon?: string | null }) =>
      api.createTag(name, icon ?? null),
    onSuccess: () => void client.invalidateQueries({ queryKey: keys.tags }),
  });
};

/**
 * A rename reaches every poll carrying the category — its category list, and for
 * some of them the primary category the app prints — so the poll lists are
 * re-read too, exactly as they are after a delete.
 */
export const useRenameTag = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) => api.renameTag(id, name),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.tags });
      void client.invalidateQueries({ queryKey: keys.polls });
    },
  });
};

/**
 * Setting a category's icon invalidates the tag list and **every poll list**:
 * the icon travels on each poll's `tags`, so a list still holding the old one
 * is the stale-after-write bug this file exists to design out.
 */
export const useSetTagIcon = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, icon }: { id: string; icon: string | null }) => api.setTagIcon(id, icon),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.tags });
      void client.invalidateQueries({ queryKey: keys.polls });
    },
  });
};

/**
 * Deleting a category changes the polls that carried it — their category list,
 * and for some of them the primary category the app prints — so both lists are
 * re-read, not just the tag list.
 */
export const useDeleteTag = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.deleteTag(id),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.tags });
      void client.invalidateQueries({ queryKey: keys.polls });
    },
  });
};

export const useCreatePoll = () =>
  usePollMutation<api.NewPoll, { poll: AdminPoll; candidates: Candidate[] }>(api.createPoll);

export const useUpdatePoll = (id: string) =>
  usePollMutation<api.PollPatch, AdminPoll>((patch) => api.updatePoll(id, patch), id);

export const useDeletePoll = (id: string) =>
  usePollMutation<string, void>((confirm) => api.deletePoll(id, confirm), id);

export const useAddCandidate = (pollId: string) =>
  usePollMutation<{ name: string; imageUrl: string }, Candidate>(
    (body) => api.addCandidate(pollId, body),
    pollId,
  );

export const useUpdateCandidate = (pollId: string) =>
  usePollMutation<{ id: string; patch: { name?: string; imageUrl?: string } }, Candidate>(
    ({ id, patch }) => api.updateCandidate(id, patch),
    pollId,
  );

export const useDeleteCandidate = (pollId: string) =>
  usePollMutation<{ id: string; force?: boolean }, void>(
    ({ id, force }) => api.deleteCandidate(id, force),
    pollId,
  );

export const useReorderCandidates = (pollId: string) =>
  usePollMutation<string[], Candidate[]>((ids) => api.reorderCandidates(pollId, ids), pollId);

/** Media writes invalidate the library; a delete can also free a poll's cover, hence both. */
function useMediaMutation<TArgs, TResult>(
  mutationFn: (args: TArgs) => Promise<TResult>,
): UseMutationResult<TResult, Error, TArgs> {
  const client = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => void client.invalidateQueries({ queryKey: keys.media }),
  });
}

export const useUploadMedia = () =>
  useMediaMutation<{ file: File; rights: api.RightsDeclaration }, { name: string; path: string }>(
    api.uploadMedia,
  );

export const useImportMedia = () =>
  useMediaMutation<
    { url: string; rights: api.RightsDeclaration },
    { name: string; path: string; source: string }
  >(api.importMedia);

export const useDeleteMedia = () => useMediaMutation<string, void>(api.deleteMedia);

export const useCompliance = () => useQuery({ queryKey: keys.compliance, queryFn: api.getCompliance });

/** A blackout moves which political polls the app shows: the poll lists are re-read too. */
function useBlackoutMutation<TArgs, TResult>(
  mutationFn: (args: TArgs) => Promise<TResult>,
): UseMutationResult<TResult, Error, TArgs> {
  const client = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: keys.compliance });
      void client.invalidateQueries({ queryKey: keys.polls });
      void client.invalidateQueries({ queryKey: ['admin', 'poll'] });
    },
  });
}

export const useAddBlackout = () => useBlackoutMutation(api.addBlackout);
export const useDeleteBlackout = () => useBlackoutMutation(api.deleteBlackout);

export const useReports = () => useQuery({ queryKey: keys.reports, queryFn: () => api.listReports() });

export const useResolveReport = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string; status: ReportStatus; resolution?: string | null }) =>
      api.resolveReport(id, body),
    onSuccess: () => void client.invalidateQueries({ queryKey: keys.reports }),
  });
};

/** Polled while Baileys is linking, until the status flips to connected on its own. */
export const useWhatsAppTransport = (linking: boolean) =>
  useQuery({
    queryKey: keys.whatsapp,
    queryFn: api.getWhatsAppTransport,
    refetchInterval: (query) => (linking && query.state.data?.status !== 'connected' ? 3000 : false),
  });

export const useSetWhatsAppTransport = () => {
  const client = useQueryClient();
  return useMutation({
    mutationFn: api.setWhatsAppTransport,
    onSuccess: (state) => client.setQueryData(keys.whatsapp, state),
  });
};

export const usePairingCode = () => useMutation({ mutationFn: api.requestPairingCode });

export type { AdminPoll, Candidate, MediaItem };
